/* SpawnAnything — parametric face painter ("Gesichts-Baukasten").
   A recipe's `face` object chooses features from fixed lists (FACE_OPTIONS); the painter draws them as
   comic pixel art straight onto the head voxels (at the fine head resolution of heads.js in the close-up
   LOD, simplified on the far LOD) and adds a few 3D parts (ears, muzzle, horns, teeth, beards).
   Bodies stay free-form (`add`); faces never need boxes any more.

   Pure module (no three.js); the compilers in voxel.js / templates/quadruped.js call faceParts().
   Coordinates of the returned 3D entries are in head units (like `add` on 'head'); `on: 'jaw'` only picks the
   jaw part (the geometry still uses the head frame). */

export const FACE_OPTIONS = {
  type: ['mensch', 'tier', 'roboter', 'monster', 'keins'],
  expression: ['neutral', 'froh', 'wuetend', 'traurig', 'ueberrascht', 'boese', 'muede', 'irre', 'entschlossen', 'arrogant', 'aengstlich'],
  eyes: ['rund', 'mandel', 'schmal', 'muede', 'gross', 'anime', 'punkte', 'schlitz', 'gluehend', 'insekt', 'alien', 'leer', 'x', 'visier', 'led', 'zyklop', 'mehrere', 'kamera'],
  brows: ['keine', 'duenn', 'normal', 'dick', 'buschig', 'monobraue'],
  nose: ['keine', 'klein', 'stups', 'knubbel', 'spitz', 'breit', 'haken', 'lang', 'clown', 'schwein'],
  mouth: ['neutral', 'laecheln', 'grinsen', 'zaehne', 'schmollmund', 'offen', 'schreien', 'ernst', 'frown', 'schief', 'fletschen', 'fangzaehne', 'gitter', 'maske', 'keiner'],
  beard: ['keiner', 'stoppeln', 'schnurrbart', 'zwirbelbart', 'walross', 'kinnbart', 'ziegenbart', 'spitzbart', 'vollbart', 'langer_bart', 'koteletten', 'pharao'],
  glasses: ['keine', 'brille', 'rund', 'sonnenbrille', 'monokel', 'augenklappe', 'skibrille'],
  ears: ['normal', 'klein', 'gross', 'spitz', 'keine', 'katze', 'hund', 'schlapp', 'rund', 'pferd', 'elefant', 'maus', 'hase'],
  cheeks: ['keine', 'rouge', 'pausbacken', 'wangenknochen'],
  age: ['jung', 'normal', 'alt'],
  marks: ['sommersprossen', 'narbe', 'narbe_wange', 'kriegsbemalung', 'kajal', 'lidschatten', 'lippenstift', 'clown', 'naht', 'tattoo', 'dreck', 'streifen', 'flecken', 'maske', 'blesse', 'tupfen'],
  muzzle: ['keine', 'katze', 'hund', 'wolf', 'baer', 'pferd', 'reptil', 'elefant', 'schwein', 'affe', 'nager', 'vogel'],
  teeth: ['keine', 'zahnreihe', 'fangzaehne', 'saebelzaehne', 'hauer', 'stosszaehne', 'giftzaehne'],
  tongue: ['keine', 'zunge', 'gespalten'],
  horns: ['keine', 'stier', 'widder', 'einhorn', 'geweih', 'teufel', 'nashorn', 'antennen', 'klein'],
  whiskers: ['keine', 'schnurrhaare'],
};
export const FACE_COLOR_KEYS = ['eyeColor', 'browColor', 'lipColor', 'beardColor', 'glassesColor', 'color', 'hornColor'];
export const FACE_KEYS = [...Object.keys(FACE_OPTIONS), ...FACE_COLOR_KEYS];
export const MAX_MARKS = 3;

/* ---------------- colours ---------------- */
const hexOf = s => s === 'team' ? -1 : s === 'team2' ? -2 : typeof s === 'number' ? s : (typeof s === 'string' && /^#[0-9a-f]{6}$/i.test(s) ? parseInt(s.slice(1), 16) : null);
const rgb = c => [(c >> 16) & 255, (c >> 8) & 255, c & 255];
const pack = (r, g, b) => (Math.max(0, Math.min(255, Math.round(r))) << 16) | (Math.max(0, Math.min(255, Math.round(g))) << 8) | Math.max(0, Math.min(255, Math.round(b)));
const mix = (a, b, t) => { if (a < 0 || b < 0) return t < 0.5 ? a : b; const A = rgb(a), B = rgb(b); return pack(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t); };
const shade = (a, f) => { if (a < 0) return a; const A = rgb(a); return pack(A[0] * f, A[1] * f, A[2] * f); };
const lumOf = c => { if (c < 0) return 0.45; const A = rgb(c); return (A[0] * 0.3 + A[1] * 0.59 + A[2] * 0.11) / 255; };
const toHex = c => '#' + (c >>> 0).toString(16).padStart(6, '0');
const BLACK = 0x16161b, WHITE = 0xf6f3ec, INK = 0x1c1a1f, RED = 0xc8202e, PINK = 0xe88a96, GOLD = 0xd9b23c;
const hashStr = s => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; };
const h2 = (x, y) => { let h = (x * 374761393 + y * 668265263) | 0; h = (h ^ (h >>> 13)) * 1274126177; return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };

/* ---------------- spec resolution (defaults per template / type / expression) ---------------- */
const EXPR = {
  neutral: { mouth: 'ernst', lid: '', brow: 'flach' },
  froh: { mouth: 'laecheln', lid: 'froh', brow: 'hoch' },
  wuetend: { mouth: 'fletschen', lid: 'wuetend', brow: 'wuetend' },
  traurig: { mouth: 'frown', lid: 'traurig', brow: 'traurig' },
  ueberrascht: { mouth: 'offen', lid: 'weit', brow: 'hoch2', pupil: 'klein' },
  boese: { mouth: 'grinsen', lid: 'wuetend', brow: 'wuetend' },
  muede: { mouth: 'ernst', lid: 'muede', brow: 'traurig' },
  irre: { mouth: 'zaehne', lid: 'irre', brow: 'skeptisch', pupil: 'klein' },
  entschlossen: { mouth: 'ernst', lid: 'eng', brow: 'wuetend1' },
  arrogant: { mouth: 'schief', lid: 'halb', brow: 'skeptisch' },
  aengstlich: { mouth: 'frown', lid: 'weit', brow: 'traurig', pupil: 'klein' },
};
const EYE_COLORS = ['#5a3a1a', '#3a6ab8', '#4a8a4a', '#6a4a2a', '#5a7a8a', '#2a2a2a'];
const pick = (v, list) => (typeof v === 'string' && list.includes(v) ? v : null);
const colorOr = (v, d) => { const c = typeof v === 'string' ? hexOf(v.toLowerCase()) : null; return c === null ? d : c; };
const baseColor = v => (typeof v === 'string' ? v : v && typeof v === 'object' ? v.color : null);

/** Full face spec (all keys, colours as ints) for a recipe; `rec.face` may be partial or absent. */
export function resolveFace(rec) {
  const tpl = rec.template || 'humanoid', f = rec.face && typeof rec.face === 'object' ? rec.face : {}, col = rec.colors || {};
  const O = FACE_OPTIONS;
  const type = pick(f.type, O.type) || (tpl === 'humanoid' ? 'mensch' : 'tier');
  const skinS = tpl === 'humanoid' ? baseColor(col.skin) || baseColor(col.torso) : baseColor(col.head) || baseColor(col.body);
  const skin = colorOr(skinS, 0xe3b48a);
  const hairC = rec.hair && typeof rec.hair === 'object' ? colorOr(rec.hair.color, null) : null;
  const expression = pick(f.expression, O.expression) || (type === 'monster' ? 'wuetend' : type === 'tier' ? 'entschlossen' : 'neutral');
  const E = EXPR[expression];
  const h = hashStr(String(rec.name || ''));
  const animal = type === 'tier';
  const robot = type === 'roboter';
  const muzzle = pick(f.muzzle, O.muzzle) || (animal ? (tpl === 'serpent' ? 'reptil' : tpl === 'quadruped' ? 'hund' : 'affe') : 'keine');
  const eyes = pick(f.eyes, O.eyes) || (robot ? 'led' : type === 'monster' ? 'gluehend' : animal ? (muzzle === 'reptil' || muzzle === 'katze' ? 'schlitz' : 'rund') : 'rund');
  const glowEye = ['gluehend', 'visier', 'led', 'zyklop', 'mehrere'].includes(eyes);
  const darkSkin = lumOf(skin) < 0.35;
  const s = {
    type, expression, eyes, muzzle,
    brows: pick(f.brows, O.brows) || (robot || animal ? (animal ? 'normal' : 'keine') : 'normal'),
    nose: pick(f.nose, O.nose) || (robot || animal ? 'keine' : 'klein'),
    mouth: pick(f.mouth, O.mouth) || (robot ? 'gitter' : animal ? 'ernst' : E.mouth),
    beard: pick(f.beard, O.beard) || 'keiner',
    glasses: pick(f.glasses, O.glasses) || 'keine',
    ears: pick(f.ears, O.ears) || (robot ? 'keine' : animal ? ({ katze: 'katze', hund: 'spitz', wolf: 'spitz', baer: 'rund', pferd: 'pferd', reptil: 'keine', elefant: 'elefant', schwein: 'schlapp', affe: 'normal', nager: 'maus', vogel: 'keine' })[muzzle] || 'spitz' : 'normal'),
    cheeks: pick(f.cheeks, O.cheeks) || 'keine',
    age: pick(f.age, O.age) || 'normal',
    marks: (Array.isArray(f.marks) ? f.marks : typeof f.marks === 'string' ? [f.marks] : []).filter(m => O.marks.includes(m)).slice(0, MAX_MARKS),
    teeth: pick(f.teeth, O.teeth) || 'keine',
    tongue: pick(f.tongue, O.tongue) || 'keine',
    horns: pick(f.horns, O.horns) || 'keine',
    whiskers: pick(f.whiskers, O.whiskers) || (animal && (muzzle === 'katze' || muzzle === 'nager') ? 'schnurrhaare' : 'keine'),
    skin,
    eyeColor: colorOr(f.eyeColor, robot ? 0x6fd4ff : glowEye ? 0xff4a2a : animal ? 0xe8b030 : hexOf(EYE_COLORS[h % EYE_COLORS.length])),
    browColor: colorOr(f.browColor, hairC !== null && hairC >= 0 ? shade(hairC, 0.8) : animal ? shade(skin, 0.55) : darkSkin ? 0x1a120c : 0x3a2416),
    lipColor: colorOr(f.lipColor, mix(shade(skin, 0.78), 0xb04040, 0.35)),
    beardColor: colorOr(f.beardColor, hairC !== null && hairC >= 0 ? hairC : 0x3a2416),
    glassesColor: colorOr(f.glassesColor, f.glasses === 'monokel' ? GOLD : INK),
    color: colorOr(f.color, robot ? 0xc6cdd5 : animal ? mix(skin, 0xfff4e0, 0.5) : 0xc8322b),
    hornColor: colorOr(f.hornColor, 0xeadfc4),
    E, glowEye,
  };
  if (s.marks.includes('lippenstift') && f.lipColor === undefined) s.lipColor = 0xc8203a;
  if (s.marks.includes('clown')) { if (!f.nose) s.nose = 'clown'; if (!f.mouth) s.mouth = 'grinsen'; s.lipColor = 0xd0202a; }
  return s;
}

/* ---------------- 2D face canvas ---------------- */
class Canvas {
  constructor(W, H) { this.W = W; this.H = H; this.px = new Array(W * H).fill(null); this.cL = Math.floor((W - 1) / 2); this.cR = Math.ceil((W - 1) / 2); }
  set(i, j, c, d = 0, e = 0) { if (i >= 0 && i < this.W && j >= 0 && j < this.H && c !== null && c !== undefined) this.px[j * this.W + i] = { c, d, e }; }
  get(i, j) { return i >= 0 && i < this.W && j >= 0 && j < this.H ? this.px[j * this.W + i] : null; }
  // column for side s (+1 = +x half, -1 = -x half) at distance dx from the centre line (dx = 0: innermost column)
  col(s, dx) { return s > 0 ? this.cR + dx : this.cL - dx; }
  m(dx, j, c, d = 0, e = 0) { this.set(this.col(1, dx), j, c, d, e); this.set(this.col(-1, dx), j, c, d, e); }
  s1(s, dx, j, c, d = 0, e = 0) { this.set(this.col(s, dx), j, c, d, e); }
  getS(s, dx, j) { return this.get(this.col(s, dx), j); }
}

/* ---------------- surface scan + apply ---------------- */
// frontmost voxel per (x, y) column of the given parts inside the frame box (z search from the frame centre forward)
function scan(L, fr, parts, rows = null, ok = () => true) {
  const vs = L.vs;
  const X0 = Math.ceil((fr.c[0] - fr.h[0]) / vs - 0.5), X1 = Math.floor((fr.c[0] + fr.h[0]) / vs - 0.5);
  const Y0 = rows ? rows[0] : Math.ceil((fr.c[1] - fr.h[1]) / vs - 0.5), Y1 = rows ? rows[1] : Math.floor((fr.c[1] + fr.h[1]) / vs - 0.5);
  const zLo = Math.floor((fr.c[2] - fr.h[2] * 0.2) / vs), zHi = Math.floor((fr.c[2] + fr.h[2] * 2.6) / vs);
  const W = X1 - X0 + 1, H = Y1 - Y0 + 1;
  const z = new Array(W * H).fill(null), part = new Array(W * H).fill(-1);
  for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
    for (let zz = zHi; zz >= zLo; zz--) {
      const v = L.get(X0 + i, Y0 + j, zz);
      if (v && parts.includes(v.p)) { if (ok(v)) { z[j * W + i] = zz; part[j * W + i] = v.p; } break; }
    }
  }
  return { X0, Y0, W, H, z, part };
}
function apply(L, S, cv) {
  for (let j = 0; j < cv.H; j++) for (let i = 0; i < cv.W; i++) {
    const p = cv.px[j * cv.W + i]; if (!p) continue;
    const z0 = S.z[j * S.W + i]; if (z0 === null) continue;
    const x = S.X0 + i, y = S.Y0 + j, part = S.part[j * S.W + i];
    if (p.d > 0) { for (let k = 1; k <= p.d; k++) L.set(x, y, z0 + k, p.c, part, p.e); L.set(x, y, z0, p.c, part, p.e); }
    else if (p.d < 0) { for (let k = 0; k < -p.d; k++) L.del(x, y, z0 - k); L.set(x, y, z0 + p.d, p.c, part, p.e); }
    else L.set(x, y, z0, p.c, part, p.e);
  }
}

/* ---------------- human-style face (mensch / monster / roboter / humanoid tier) ---------------- */
// rows/columns are derived from head-unit positions so the layout matches the 3D parts (ears, glasses temples)
export const HUMAN_ANCHORS = { eye: 0.04, brow: 0.36, nose: -0.26, mouth: -0.52 };
const rowOf = (H, v) => Math.max(0, Math.min(H - 1, Math.round((v + 1) / 2 * H - 0.5)));

/* Eye sprites for the +x eye, column 0 = inner corner, rows top -> bottom.
   L lash/lid line, W white, I iris, P pupil, B black, H fixed highlight, G glow, g bright glow, K skin, S lid shadow, D dark */
const EYE_SPRITES = {
  rund: ['LLLLLL', 'WIPPIW', 'WIPPIW', 'WWIIWW', '.WWWW.'],
  gross: ['.LLLL.', 'LWIIWL', 'WIPPIW', 'WIPPIW', 'WWIIWW', '.WWWW.'],
  mandel: ['.LLLLLL', 'WIPPIW.', '.WIIW..'],
  schmal: ['LLLLLL', 'WWPPWW'],
  muede: ['SSSSSS', 'LLLLLL', 'WIPPIW', '.WWWW.'],
  anime: ['.LLLLLL', 'LIPPIW.', 'WIPPIW.', 'WIPPIW.', 'WIIIIW.', 'WWHIWW.', '.WWWW..'],
  punkte: ['BB', 'BB', 'BB'],
  schlitz: ['.LLLL.', 'IIPPII', 'IIPPII', 'IIPPII', '.IPPI.'],
  gluehend: ['DGGGGD', 'GggggG', 'DGGGGD'],
  insekt: ['.BBBB.', 'BHBBDB', 'BBDBBB', 'BDBBDB', 'BBBDBB', '.BBBB.'],
  alien: ['.....BBBB', '...BBBBBB', '.BBBHBBBB', 'BBBHBBBBB', 'BBBBBBBB.', '.BBBBBB..', '..BBB....'],
  leer: ['LLLLLL', 'WWWWWW', 'WWWWWW', '.WWWW.'],
  x: ['D...D', '.D.D.', '..D..', '.D.D.', 'D...D'],
  led: ['DDDDDD', 'DggggD', 'DDDDDD'],
  kamera: ['.DDDD.', 'DIIIID', 'DIggID', 'DIggID', 'DIIIID', '.DDDD.'],
};
const EYE_MID = { rund: ['LLL', 'WPW'], gross: ['LLL', 'WPW', 'WWW'], mandel: ['LLLL', 'WPW.'], schmal: ['LLL'], muede: ['SSS', 'LPL'], anime: ['LLL', 'IPI', 'WIW'],
  punkte: ['B', 'B'], schlitz: ['III', 'IPI'], gluehend: ['GgG'], insekt: ['BBB', 'BHB'], alien: ['.BB', 'BBB'], leer: ['LLL', 'WWW'], x: ['D.D', '.D.'], led: ['ggg'], kamera: ['DgD'] };

function drawHumanEyes(cv, s, A, tier) {
  const W = cv.W, sc = W / 24, st = s.eyes, E = s.E;
  const ey = A.ey, skin = s.skin, lidC = shade(skin, 0.8), lash = INK;
  if (tier === 'lo') {
    // far LOD: one or two pixels per eye, no whites
    const dx = Math.max(1, Math.round(W * 0.2) - 1);
    const c = s.glowEye ? s.eyeColor : st === 'leer' ? WHITE : INK, e = s.glowEye ? 1 : 0;
    if (st === 'visier') { for (let d = 0; d <= dx + 1; d++) cv.m(d, ey, c, 0, 1); return [[ey, ey, 0, dx + 1]]; }
    if (st === 'zyklop') { cv.m(0, ey, c, 0, 1); return [[ey, ey, 0, 0]]; }
    cv.m(dx, ey, c, 0, e);
    if (W >= 8) cv.m(dx + 1, ey, c, 0, e);
    return [[ey, ey, dx, dx + (W >= 8 ? 1 : 0)]];
  }
  let d0 = Math.max(1, Math.round(W * 0.085));
  // robot specials
  if (st === 'visier') {
    const x1 = Math.round(W / 2 - 2 * sc);
    for (let d = 0; d <= x1; d++) { cv.m(d, ey + 1, INK, 1); cv.m(d, ey - 1, INK, 1); cv.m(d, ey, mix(s.eyeColor, 0xffffff, d % 3 === 0 ? 0.45 : 0.1), 1, 1); }
    cv.m(x1 + 1, ey, INK, 1); cv.m(x1 + 1, ey + 1, INK, 1); cv.m(x1 + 1, ey - 1, INK, 1);
    return [[ey - 1, ey + 1, 0, x1 + 1]];
  }
  if (st === 'zyklop') {
    const r = Math.max(2, Math.round(3.2 * sc)), cy = ey;
    for (let dy = -r - 1; dy <= r + 1; dy++) for (let dx = 0; dx <= r + 1; dx++) {
      const dd = Math.hypot(dx + 0.5, dy);
      if (dd <= r - 1.2) cv.m(dx, cy + dy, mix(s.eyeColor, 0xffffff, dd < 1.2 ? 0.7 : 0.15), 1, 1);
      else if (dd <= r) cv.m(dx, cy + dy, s.eyeColor, 1, 1);
      else if (dd <= r + 1.1) cv.m(dx, cy + dy, INK, 1);
    }
    return [[cy - r - 1, cy + r + 1, 0, r + 1]];
  }
  if (st === 'mehrere') {
    const pts = [[d0, ey], [d0 + Math.round(3 * sc), ey + Math.round(2 * sc)], [d0 + Math.round(4 * sc), ey - Math.round(1 * sc)]];
    for (const [dx, y] of pts) { cv.m(dx, y, s.eyeColor, 1, 1); cv.m(dx + 1, y, mix(s.eyeColor, 0xffffff, 0.4), 1, 1); cv.m(dx, y + 1, INK, 1); cv.m(dx + 1, y + 1, INK, 1); }
    return [[ey - 2, ey + 3, d0, d0 + 5]];
  }
  const spr = (tier === 'mid' ? EYE_MID[st] : EYE_SPRITES[st]) || (tier === 'mid' ? EYE_MID.rund : EYE_SPRITES.rund);
  if (st === 'alien' || st === 'insekt') d0 = Math.max(1, Math.round(W * 0.05));
  if (tier === 'mid') d0 = Math.max(1, Math.round(W * 0.1));
  const lid = E.lid, small = E.pupil === 'klein';
  const colorOf = ch => ({ L: lash, W: WHITE, I: s.eyeColor, P: INK, B: 0x0e0f14, H: 0xffffff, G: s.eyeColor, g: mix(s.eyeColor, 0xffffff, 0.55), K: skin, S: lidC, D: mix(INK, s.eyeColor, 0.15) })[ch];
  const box = [];
  for (const sd of [1, -1]) {
    let rows = spr.slice();
    if (lid === 'weit' && tier !== 'mid' && rows.length > 2) rows = [rows[0], rows[1], ...rows.slice(1)];
    if (lid === 'irre' && sd > 0 && tier !== 'mid') rows = [rows[0], ...rows.slice(1).flatMap((r, i) => i === 1 ? [r, r] : [r])];
    const h = rows.length, w = Math.max(...rows.map(r => r.length));
    // grid[b][a], b = 0 top
    const g = rows.map(r => Array.from({ length: w }, (_, a) => r[a] && r[a] !== '.' ? r[a] : null));
    if (small) for (const r of g) for (let a = 0; a < w; a++) if (r[a] === 'P' || r[a] === 'I') r[a] = 'W';
    if (small) { const b = Math.floor(h / 2), a = Math.floor((w - 1) / 2); for (const aa of w % 2 ? [a] : [a, a + 1]) if (g[b][aa]) g[b][aa] = 'P'; }
    const glowSt = st === 'gluehend' || st === 'led' || st === 'kamera';
    // lids: hide rows from the top per column (angry: inner side, sad: outer side, tired: flat)
    const cut = a => {
      const t = w > 1 ? a / (w - 1) : 0, n = h;
      if (lid === 'wuetend') return Math.round((1 - t) * n * 0.55 + 0.2);
      if (lid === 'eng') return Math.round((1 - t) * n * 0.35 + 0.45);
      if (lid === 'traurig') return Math.round(t * n * 0.45);
      if (lid === 'muede' && st !== 'muede') return Math.max(1, Math.round(n * 0.45));
      if (lid === 'halb') return Math.max(1, Math.round(n * 0.4));
      return 0;
    };
    const tallSt = !['punkte', 'x', 'insekt', 'alien'].includes(st);
    for (let a = 0; a < w; a++) {
      const k = tallSt && h >= 3 ? Math.min(h - 2, cut(a)) : 0; // keep at least lash + one row open
      let first = true;
      for (let b = 0; b < h; b++) {
        if (!g[b][a]) continue;
        if (b < k) { g[b][a] = 'S'; continue; }
        if (first && k > 0 && !glowSt) g[b][a] = 'L';
        first = false;
      }
    }
    if (lid === 'froh' && tallSt) for (let a = 0; a < w; a++) { const b = h - 1; if (g[b][a]) g[b][a] = 'K'; if ((a === 0 || a === w - 1) && h > 2 && g[b - 1][a] && g[b - 1][a] !== 'L') g[b - 1][a] = 'K'; }
    // catch light: the pupil pixel nearest to the screen-left (= -x) on the topmost pupil row
    let hl = null;
    for (let b = 0; b < h && !hl; b++) for (let a = 0; a < w; a++) if (g[b][a] === 'P' || (st === 'punkte' && g[b][a] === 'B')) { const col = cv.col(sd, d0 + a); if (!hl || col < hl[2]) hl = [b, a, col]; }
    if (hl && !small) g[hl[0]][hl[1]] = 'H';
    const top = ey + Math.floor((h - 1) / 2);
    for (let b = 0; b < h; b++) for (let a = 0; a < w; a++) {
      const ch = g[b][a]; if (!ch) continue;
      const e = (ch === 'G' || ch === 'g') && s.glowEye ? 1 : 0;
      cv.s1(sd, d0 + a, top - b, colorOf(ch), 0, e);
    }
    box.push([top - h + 1, top, d0, d0 + w - 1]);
  }
  return box;
}

function drawBrows(cv, s, A, eyeBox, tier) {
  const st = s.brows; if (st === 'keine') return;
  const W = cv.W, E = s.E, c = s.browColor;
  const [b0, b1, x0, x1] = eyeBox[0];
  const th = tier === 'lo' ? 1 : st === 'duenn' ? 1 : st === 'normal' ? 1 : st === 'dick' ? 2 : 2;
  const base = b1 + (tier === 'lo' ? 1 : 2);
  const from = tier === 'lo' ? x0 : Math.max(0, x0 - 1), to = x1 + 1, n = to - from;
  for (const sd of [1, -1]) {
    for (let k = 0; k <= n; k++) {
      const t = n ? k / n : 0; // 0 inner .. 1 outer
      let off = 0;
      switch (E.brow) {
        case 'wuetend': off = Math.round(-1.4 + 2.4 * t); break;
        case 'wuetend1': off = Math.round(-0.8 + 1.2 * t); break;
        case 'traurig': off = Math.round(1.3 - 2.2 * t); break;
        case 'hoch': off = 1 + (t > 0.2 && t < 0.8 ? 1 : 0) - (t > 0.95 ? 1 : 0); break;
        case 'hoch2': off = 2 + (t > 0.2 && t < 0.85 ? 1 : 0); break;
        case 'skeptisch': off = sd > 0 ? 2 + (t > 0.3 && t < 0.8 ? 1 : 0) : Math.round(-0.6 + 0.8 * t); break;
        default: off = t > 0.85 ? -1 + (tier === 'lo' ? 1 : 0) : 0;
      }
      if (tier === 'lo') off = Math.max(0, Math.min(1, Math.sign(off)));
      const y = base + off;
      let thick = th + (st === 'normal' && t < 0.4 && tier !== 'lo' ? 1 : 0) + (st === 'buschig' && h2(k, sd) > 0.55 ? 1 : 0);
      for (let q = 0; q < thick; q++) cv.s1(sd, from + k, y + q, st === 'buschig' && q === thick - 1 ? shade(c, 1.15) : c, tier === 'lo' ? 0 : 1);
    }
    if (st === 'monobraue') for (let k = 0; k < from; k++) for (let q = 0; q < th; q++) cv.m(k, base + (E.brow === 'wuetend' ? -1 : 0) + q, c, 1);
  }
}

function drawNose(cv, s, A, tier) {
  const st = s.nose; if (st === 'keine') return;
  const ny = A.ny, sk = s.skin, dk = shade(sk, 0.66), hl = mix(sk, 0xffffff, 0.16);
  if (tier === 'lo') {
    if (st === 'clown') cv.m(0, ny, 0xe02a2a, 1);
    else if (st === 'schwein') cv.m(0, ny, mix(sk, 0xf0a0b0, 0.5), 1);
    else if (st === 'lang' || st === 'haken' || st === 'knubbel' || st === 'breit') cv.m(0, ny, sk, 1);
    return;
  }
  const W = cv.W, k = W >= 30 ? 1 : 0;
  const sh = shade(sk, 0.8), nos = shade(sk, 0.45);
  switch (st) {
    case 'klein': cv.m(0, ny + 2, hl, 1); cv.m(0, ny + 1, sk, 1); cv.m(0, ny, sk, 1); cv.s1(1, 1, ny + 2, dk); cv.s1(1, 1, ny + 1, dk); cv.m(1, ny, nos); cv.m(0, ny - 1, sh); break;
    case 'stups': cv.m(0, ny + 1, hl, 1); cv.m(0, ny, sk, 1); cv.m(1, ny, sk, 1); cv.m(1, ny - 1, nos); cv.m(0, ny - 1, sh); break;
    case 'knubbel': { const c = mix(sk, 0xd85a4a, 0.25); cv.m(0, ny + 3, sk, 1); cv.m(0, ny + 2, c, 1); cv.m(0, ny + 1, mix(c, 0xffffff, 0.15), 2); cv.m(0, ny, c, 2); cv.m(1, ny + 1, c, 1); cv.m(1, ny, c, 1); cv.m(2, ny, sh); cv.m(0, ny - 1, sh); cv.m(1, ny - 1, sh); break; }
    case 'spitz': for (let k = 1; k <= 4; k++) cv.m(0, ny + k, k === 3 ? hl : sk, 1); cv.m(0, ny, sk, 2); cv.s1(1, 1, ny + 2, dk); cv.s1(1, 1, ny + 1, dk); cv.m(1, ny, dk); cv.m(0, ny - 1, sh); break;
    case 'breit': cv.m(0, ny + 2, hl, 1); cv.m(0, ny + 1, sk, 1); cv.m(0, ny, sk, 1); cv.m(1, ny, sk, 1); cv.m(1, ny + 1, dk); cv.m(2, ny, nos); cv.m(0, ny - 1, sh); cv.m(1, ny - 1, sh); break;
    case 'haken': for (let k = 1; k <= 4; k++) cv.m(0, ny + k, k === 3 ? hl : sk, k <= 2 ? 2 : 1); cv.m(0, ny, sk, 2); cv.m(0, ny - 1, dk, 1); cv.s1(1, 1, ny + 2, dk); cv.m(1, ny, nos); cv.m(1, ny - 1, sh); break;
    case 'lang': for (let k = 1; k <= 3; k++) cv.m(0, ny + k, k === 2 ? hl : sk, 1); cv.m(0, ny, sk, 4 + k); cv.m(1, ny, dk); cv.m(0, ny - 1, sh); break;
    case 'clown': { const c = 0xe02a2a; for (let dy = -1; dy <= 2; dy++) for (let dx = 0; dx <= 1; dx++) cv.m(dx, ny + dy, dy === 2 && dx === 0 ? 0xff9a9a : c, (dx === 1 && (dy === -1 || dy === 2)) ? 1 : 2); break; }
    case 'schwein': { const c = mix(sk, 0xf0a0b0, 0.55); for (let dy = -1; dy <= 1; dy++) for (let dx = 0; dx <= 2; dx++) if (!(dx === 2 && dy !== 0)) cv.m(dx, ny + dy, c, 1); cv.m(1, ny, shade(c, 0.45), 1); break; }
  }
}

function drawMouth(cv, s, A, tier) {
  const st = s.mouth; if (st === 'keiner' || st === 'maske') return;
  const my = A.my, W = cv.W, sc = W / 24;
  const lip = s.lipColor, dk = mix(lip, INK, 0.55), teeth = 0xf4f1e6, tongue = 0xd04a5a, inside = 0x3a1018;
  if (tier === 'lo') {
    if (st === 'offen' || st === 'schreien') { cv.m(0, my, inside, -1); if (W >= 8) cv.m(0, my - 1, inside, -1); }
    else if (st === 'zaehne' || st === 'fletschen' || st === 'grinsen') { cv.m(0, my, teeth); cv.m(1, my, dk); }
    else if (st === 'gitter') { cv.m(0, my, INK); cv.m(1, my, INK); }
    else if (st !== 'neutral' && st !== 'maske') cv.m(0, my, mix(dk, s.skin, 0.35));
    return;
  }
  const mw = Math.max(2, Math.round(3 * sc)); // half width in columns
  switch (st) {
    case 'neutral': for (let d = 0; d < mw - 1; d++) cv.m(d, my, dk); break;
    case 'ernst': for (let d = 0; d < mw; d++) cv.m(d, my, dk); for (let d = 0; d < mw - 1; d++) cv.m(d, my - 1, mix(s.skin, lip, 0.35)); break;
    case 'laecheln': for (let d = 0; d < mw; d++) cv.m(d, my, dk); cv.m(mw, my + 1, dk); for (let d = 0; d < mw - 1; d++) cv.m(d, my - 1, mix(s.skin, lip, 0.4)); break;
    case 'grinsen':
      for (let d = 0; d <= mw; d++) cv.m(d, my, dk);
      cv.m(mw + 1, my + 1, dk);
      for (let d = 0; d < mw; d++) cv.m(d, my - 1, inside, -1);
      for (let d = 0; d < mw - 1; d++) cv.m(d, my - 2, mix(s.skin, lip, 0.45));
      break;
    case 'zaehne':
      for (let d = 0; d <= mw; d++) { cv.m(d, my + 1, dk); cv.m(d, my, teeth); cv.m(d, my - 1, d < mw ? inside : dk, d < mw ? -1 : 0); }
      cv.m(mw + 1, my + 1, dk); cv.m(mw + 1, my + 2, dk);
      for (let d = 0; d < mw; d++) cv.m(d, my - 2, dk);
      break;
    case 'schmollmund':
      for (let d = 0; d < Math.max(2, mw - 1); d++) { cv.m(d, my + 1, shade(lip, 0.9), 1); cv.m(d, my, dk, 1); cv.m(d, my - 1, mix(lip, 0xffffff, 0.15), 1); }
      break;
    case 'offen': {
      const ow = Math.max(1, mw - 1);
      for (let d = 0; d <= ow; d++) for (let y = my - 2; y <= my + 1; y++) {
        const corner = d === ow && (y === my - 2 || y === my + 1);
        if (corner) continue;
        cv.m(d, y, y === my + 1 ? teeth : y === my - 2 ? tongue : inside, -1);
      }
      for (let d = 0; d <= ow; d++) { cv.m(d, my + 2, d === ow ? s.skin : dk); cv.m(d, my - 3, d === ow ? s.skin : mix(s.skin, lip, 0.5)); }
      cv.m(ow + 1, my, dk); cv.m(ow + 1, my - 1, dk);
      break;
    }
    case 'schreien': {
      for (let d = 0; d <= mw; d++) for (let y = my - 3; y <= my + 1; y++) {
        const corner = d === mw && (y <= my - 2 || y === my + 1);
        if (corner) continue;
        cv.m(d, y, y === my + 1 ? teeth : y === my - 3 ? tongue : inside, y === my + 1 ? -1 : -2);
      }
      for (let d = 0; d <= mw; d++) { cv.m(d, my + 2, dk); }
      cv.m(mw + 1, my, dk); cv.m(mw + 1, my - 1, dk); cv.m(mw, my - 3, dk); cv.m(mw, my - 2, dk);
      for (let d = 0; d < mw; d++) cv.m(d, my - 4, dk);
      break;
    }
    case 'frown': for (let d = 0; d < mw; d++) cv.m(d, my, dk); cv.m(mw, my - 1, dk); for (let d = 0; d < mw - 1; d++) cv.m(d, my - 1, mix(s.skin, lip, 0.35)); break;
    case 'schief':
      for (let d = 0; d < mw; d++) cv.m(d, my, dk);
      cv.s1(1, mw, my + 1, dk); cv.s1(1, mw + 1, my + 2, dk);
      cv.s1(-1, mw - 1, my, s.skin);
      break;
    case 'fletschen':
      for (let d = 0; d <= mw; d++) {
        cv.m(d, my + 1, dk); cv.m(d, my - 2, dk);
        cv.m(d, my, (d % 2 === 1) ? mix(teeth, INK, 0.5) : teeth); cv.m(d, my - 1, (d % 2 === 1) ? mix(teeth, INK, 0.5) : teeth);
      }
      cv.m(mw + 1, my, dk); cv.m(mw + 1, my - 1, dk);
      break;
    case 'fangzaehne':
      for (let d = 0; d < mw; d++) cv.m(d, my, dk);
      cv.m(mw, my + 1, dk);
      cv.m(Math.max(1, mw - 2), my - 1, teeth, 1); cv.m(Math.max(1, mw - 2), my - 2, teeth, 1);
      break;
    case 'gitter': {
      const gw = Math.max(2, mw + 1);
      for (let d = 0; d <= gw; d++) for (let y = my - 2; y <= my + 1; y++) cv.m(d, y, d === gw || y === my + 1 || y === my - 2 ? INK : ((y - my) % 2 === 0 ? 0x2a2e36 : mix(s.color, 0xffffff, 0.2)), 1);
      break;
    }
  }
}

/* beards painted on the face (the 3D hanging parts are added as ops in faceParts) */
function drawBeard(cv, s, A, tier) {
  const st = s.beard; if (st === 'keiner' || st === 'pharao') return;
  const W = cv.W, H = cv.H, my = A.my, c = s.beardColor, d2 = shade(c, 0.8);
  const half = Math.ceil(W / 2), sc = W / 24, mw = Math.max(2, Math.round(3 * sc));
  const tex = (d, y) => (h2(d * 7 + 3, y * 3 + 1) > 0.72 ? d2 : c);
  const lo = tier === 'lo';
  const must = (d, y, dd = 1) => cv.m(d, y, tex(d, y), lo ? 0 : dd);
  const moustache = (wide = 0, droop = 0, up = 0) => {
    const n = mw + wide;
    for (let d = 0; d <= n; d++) { must(d, my + 1); if (d < n - 1 || lo) must(d, my + 2); }
    if (droop) for (let k = 1; k <= droop; k++) { must(n, my + 1 - k); must(n - 1, my + 1 - k); }
    if (up) { cv.m(n + 1, my + 2, c, lo ? 0 : 1); cv.m(n + 2, my + 3, c, lo ? 0 : 1); cv.m(n + 2, my + 4, d2, lo ? 0 : 1); }
  };
  const goatee = (w = 1, upTo = my - 2) => { for (let y = 0; y <= upTo; y++) for (let d = 0; d <= w; d++) must(d, y); };
  switch (st) {
    case 'stoppeln':
      if (lo) break;
      for (let y = 0; y <= my + 2; y++) for (let d = 0; d < half; d++) {
        if (y >= my - 1 && y <= my && d < mw + 1) continue;
        if (y > my - 2 && d > half - 3 && y > A.ny) continue;
        if ((d + y) % 2 === 0 && h2(d * 3 + 1, y * 5) > 0.5) cv.m(d, y, mix(s.skin, c, 0.22));
      }
      break;
    case 'schnurrbart': moustache(); break;
    case 'zwirbelbart': moustache(0, 0, 1); break;
    case 'walross': moustache(1, 3); break;
    case 'kinnbart': moustache(); goatee(Math.max(1, Math.round(1.5 * sc)), my - 2); break;
    case 'ziegenbart': goatee(1, my - 2); break;
    case 'spitzbart': moustache(); goatee(1, my - 2); break;
    case 'koteletten':
      for (let y = my; y <= A.ey; y++) for (let d = half - 2; d < half; d++) must(d, y);
      break;
    case 'vollbart': case 'langer_bart':
      for (let y = 0; y <= my + 2; y++) for (let d = 0; d < half; d++) {
        const mouthHole = y >= my - 1 && y <= my && d <= mw;
        if (mouthHole) continue;
        if (y > my + 1 && d < mw + 1 && y > my + 2) continue;
        must(d, y, y <= 1 ? 2 : 1);
      }
      for (let y = my + 3; y <= A.ny + 1; y++) for (let d = half - 3; d < half; d++) must(d, y);
      for (let y = A.ny + 2; y <= A.ey - 2; y++) for (let d = half - 2; d < half; d++) must(d, y);
      break;
  }
}

function drawMarks(cv, s, A, eyeBox, tier, late) {
  const W = cv.W, half = Math.ceil(W / 2), lo = tier === 'lo';
  const [b0, b1, x0, x1] = eyeBox[0];
  for (const mk of s.marks) {
    const isLate = ['narbe', 'narbe_wange', 'naht', 'kajal', 'lippenstift'].includes(mk);
    if (isLate !== late) continue;
    switch (mk) {
      case 'sommersprossen': {
        const c = mix(s.skin, 0x8a4a2a, 0.45);
        for (let y = A.ny; y <= b0 - 1; y++) for (let d = 0; d <= x1 + 1; d++) if (h2(d * 5 + 1, y * 9 + 2) > (lo ? 0.9 : 0.68)) { cv.s1(1, d, y, c); if (h2(d * 3, y * 7) > 0.5) cv.s1(-1, d, y, c); }
        break;
      }
      case 'narbe': {
        const c = 0xb85a5a, n = lo ? 3 : b1 - b0 + 6;
        for (let k = 0; k < n; k++) { const d = x0 + Math.round(k * (x1 - x0 + 1) / n), y = b1 + 3 - k; cv.s1(1, d, y, c); if (!lo && k % 2 === 1) cv.s1(1, d + 1, y, mix(c, 0xffffff, 0.4)); }
        break;
      }
      case 'narbe_wange': {
        const c = 0xb85a5a;
        for (let k = 0; k < (lo ? 2 : 4); k++) cv.s1(-1, x0 + 1 + k, A.ny + 1 - k, c);
        break;
      }
      case 'kriegsbemalung': {
        const c = s.color;
        for (let k = 0; k < (lo ? 1 : 2); k++) for (let d = x0; d <= x1 + 1; d++) cv.m(d, A.ny + 1 - k * 2, c);
        if (!lo) for (let y = b1 + 1; y <= b1 + 5; y++) cv.m(0, y, c);
        break;
      }
      case 'kajal': {
        if (lo) break;
        for (let d = x0 - 1; d <= x1 + 1; d++) cv.m(d, b0 - 1, mix(INK, s.skin, 0.25));
        cv.m(x1 + 1, b1, INK); cv.m(x1 + 2, b1, INK); cv.m(x1 + 3, b1, INK); cv.m(x1 + 2, b1 - 1, INK);
        for (let d = x0; d <= x1; d++) { const p = cv.getS(1, d, b1); if (p) cv.m(d, b1, INK); }
        break;
      }
      case 'lidschatten': {
        const c = s.color;
        for (let d = x0; d <= x1; d++) { cv.m(d, b1 + 1, c); if (!lo) cv.m(d, b1 + 2, mix(c, s.skin, 0.5)); }
        break;
      }
      case 'clown': {
        // white base is applied in faceCanvas; blue diamonds above/below the eyes
        const c = 0x3a6ad8, mx = Math.round((x0 + x1) / 2);
        cv.m(mx, b1 + 1, c); cv.m(mx, b1 + 2, c); cv.m(mx, b0 - 1, c); cv.m(mx, b0 - 2, c);
        break;
      }
      case 'naht': {
        const y = b1 + 4, c = 0x3a2a22;
        for (let d = 0; d < half - 1; d++) { cv.m(d, y, c); if (!lo && d % 2 === 0) { cv.m(d, y + 1, mix(c, s.skin, 0.4)); cv.m(d, y - 1, mix(c, s.skin, 0.4)); } }
        break;
      }
      case 'tattoo': {
        const c = mix(s.color, INK, 0.3);
        const pts = [[0, 0], [1, 0], [2, 1], [2, 2], [1, 3], [0, 2], [1, 1]];
        for (const [dx, dy] of pts) cv.s1(-1, x1 + dx - 1, A.ny + dy - 1, c);
        break;
      }
      case 'dreck': {
        const c = mix(s.skin, 0x4a3a22, 0.45);
        for (let y = 0; y < cv.H; y++) for (let d = 0; d < half; d++) if (h2(d * 13 + 5, y * 11 + 7) > 0.88) cv.s1(h2(d, y) > 0.5 ? 1 : -1, d, y, c);
        break;
      }
      case 'streifen': {
        const c = mix(s.skin, INK, 0.75);
        for (let k = 0; k < 3; k++) { const y = b1 + 2 + k * 2; cv.m(k, y, c); cv.m(k + 1, y + 1, c); }
        for (let k = 0; k < 3; k++) { const y = b0 - 1 - k * 2; for (let d = half - 3; d < half; d++) cv.m(d, y + (d - half + 3 > 1 ? 1 : 0), c); }
        break;
      }
      case 'flecken': case 'tupfen': {
        const c = mk === 'flecken' ? mix(s.skin, INK, 0.7) : mix(s.skin, 0xffffff, 0.55);
        for (let y = 0; y < cv.H; y++) for (let d = 0; d < half; d++) if (h2(d * 17 + 3, y * 5 + 9) > 0.9) cv.s1(h2(d + 1, y) > 0.5 ? 1 : -1, d, y, c);
        break;
      }
      case 'maske': {
        const c = s.type === 'tier' ? mix(s.skin, INK, 0.8) : INK;
        for (let y = b0 - 1; y <= b1 + 1; y++) for (let d = x0 - 1; d <= x1 + 1; d++) { const p = cv.getS(1, d, y); if (!p || p.c === s.skin) cv.m(d, y, c); }
        break;
      }
      case 'blesse': {
        for (let y = A.ny; y < cv.H - 1; y++) cv.m(0, y, WHITE);
        if (!lo) for (let y = b0; y < cv.H - 2; y++) cv.m(1, y, WHITE);
        break;
      }
    }
  }
}

function drawCheeksAge(cv, s, A, eyeBox, tier) {
  if (tier === 'lo') return;
  const [b0, b1, x0, x1] = eyeBox[0], sk = s.skin, dk = shade(sk, 0.85);
  if (s.cheeks === 'rouge' || s.cheeks === 'pausbacken') { const c = mix(sk, 0xe8606a, 0.42); cv.m(x1 - 1, b0 - 2, c, s.cheeks === 'pausbacken' ? 1 : 0); cv.m(x1, b0 - 2, c, s.cheeks === 'pausbacken' ? 1 : 0); if (s.cheeks === 'pausbacken') { cv.m(x1 + 1, b0 - 2, sk, 1); cv.m(x1, b0 - 3, sk, 1); cv.m(x1 - 1, b0 - 3, sk, 1); } }
  if (s.cheeks === 'wangenknochen') for (let k = 0; k < 3; k++) cv.m(x1 - k, b0 - 2 - k, dk);
  if (s.age === 'alt') {
    cv.m(x1 + 1, b1, dk); cv.m(x1 + 2, b1 + 1, dk); cv.m(x1 + 1, b0, dk); cv.m(x1 + 2, b0 - 1, dk);
    for (let d = x0; d <= x1; d++) if (d % 2 === 0 || d === x0) cv.m(d, b0 - 1, dk);
    const n = A.ny - A.my;
    for (let k = 0; k <= n; k++) cv.m(2 + Math.round(k * 1.2), A.ny - k, dk);
    for (const y of [b1 + 4, b1 + 6]) for (let d = 0; d <= x1; d++) if ((d + y) % 3 !== 0) cv.m(d, y, dk);
  }
}

function drawGlasses(cv, s, eyeBox, tier) {
  const g = s.glasses; if (g === 'keine') return;
  const [b0, b1, x0, x1] = eyeBox[0], c = s.glassesColor, lo = tier === 'lo';
  const fr = (sd, round, fill) => {
    for (let d = x0 - 1; d <= x1 + 1; d++) for (let y = b0 - 1; y <= b1 + 1; y++) {
      const edge = d === x0 - 1 || d === x1 + 1 || y === b0 - 1 || y === b1 + 1;
      const corner = (d === x0 - 1 || d === x1 + 1) && (y === b0 - 1 || y === b1 + 1);
      if (round && corner) continue;
      if (edge) cv.s1(sd, d, y, c, lo ? 0 : 1);
      else if (fill !== undefined) cv.s1(sd, d, y, (!lo && d === x0 && y === b1) ? mix(fill, 0xffffff, 0.45) : fill, lo ? 0 : 1);
    }
  };
  switch (g) {
    case 'brille': case 'rund': fr(1, g === 'rund'); fr(-1, g === 'rund'); for (let d = 0; d < x0 - 1; d++) cv.m(d, b1, c, lo ? 0 : 1); break;
    case 'sonnenbrille': fr(1, false, 0x1a1d24); fr(-1, false, 0x1a1d24); for (let d = 0; d < x0 - 1; d++) cv.m(d, b1, c, lo ? 0 : 1); break;
    case 'skibrille': { const t = mix(c, 0xffffff, 0.25); fr(1, true, t); fr(-1, true, t); for (let d = 0; d < x0 - 1; d++) { cv.m(d, b1, c, 1); cv.m(d, b0, c, 1); } break; }
    case 'monokel': {
      const gc = s.glassesColor;
      for (let d = x0 - 1; d <= x1 + 1; d++) for (let y = b0 - 1; y <= b1 + 1; y++) {
        const edge = d === x0 - 1 || d === x1 + 1 || y === b0 - 1 || y === b1 + 1, corner = (d === x0 - 1 || d === x1 + 1) && (y === b0 - 1 || y === b1 + 1);
        if (edge && !corner) cv.s1(1, d, y, gc, lo ? 0 : 1);
      }
      if (!lo) for (let k = 1; k <= 4; k++) cv.s1(1, x1 + 1 + (k > 2 ? 1 : 0), b0 - 1 - k, gc);
      break;
    }
    case 'augenklappe': {
      for (let d = x0 - 1; d <= x1 + 1; d++) for (let y = b0 - 1; y <= b1 + 1; y++) if (!((d === x0 - 1 || d === x1 + 1) && (y === b0 - 1 || y === b1 + 1))) cv.s1(1, d, y, 0x141418, lo ? 0 : 1);
      if (!lo) for (let k = 1; k <= cv.H; k++) { const d = x1 + 1 + k, y = b1 + 1 + k; if (cv.col(1, d) >= cv.W) break; cv.s1(1, d, y, 0x141418, 1); }
      if (!lo) for (let k = 1; k <= cv.W; k++) { const d = x0 - 1 - k, y = b1 + 1 + Math.round(k * 0.5); cv.s1(1, d, y, 0x141418, 1); }
      break;
    }
  }
}

/* robots: panel seams, bolts and the faceplate */
function drawRobotPlate(cv, s, A, tier) {
  if (tier === 'lo') { if (s.mouth === 'maske') cv.m(0, A.my, s.color); return; }
  const W = cv.W, half = Math.ceil(W / 2), seam = shade(s.skin, 0.62);
  for (let y = 1; y < A.ey - 2; y++) cv.m(half - 3, y, seam);
  cv.m(half - 2, A.ey + 2, mix(s.skin, 0xffffff, 0.5), 1);
  if (s.mouth === 'maske') {
    const top = A.ny + 1, pw = Math.max(3, Math.round(W * 0.26));
    for (let y = 0; y <= top; y++) for (let d = 0; d <= pw; d++) {
      const edge = d === pw || y === top;
      cv.m(d, y, edge ? shade(s.color, 0.75) : (d % 2 === 1 && y > 1 && y < top - 1 ? shade(s.color, 0.6) : s.color), 1);
    }
    cv.m(0, top + 1, s.color, 1);
  }
}

/* ---------------- animal front (quadruped / serpent / humanoid with muzzle) ---------------- */
function drawAnimal(cv, s, A, tier, S) {
  const W = cv.W, H = cv.H, lo = tier === 'lo', sc = W / 24;
  const fur = s.skin, dk = shade(fur, 0.55), E = s.E;
  // eyes: the human sprites (bigger, with iris + catch light); round animal eyes use the big variant
  const ey = A.ey, st = s.eyes;
  const s2 = { ...s, eyes: st === 'rund' ? 'gross' : st };
  const box = drawHumanEyes(cv, s2, { ey }, tier);
  if (s.muzzle === 'pferd' && box.length && !lo) {
    // horses look sideways: shift the eyes to the outer edge of the face
    const shift = Math.max(0, Math.floor(W / 2) - 1 - box[0][3]);
    if (shift) { const tmp = new Canvas(W, H); for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) { const p = cv.get(i, j); if (p) tmp.set(i < W / 2 ? i - shift : i + shift, j, p.c, p.d, p.e); } cv.px = tmp.px; }
  }
  if (!lo && box.length) {
    const [b0, b1, x0, x1] = box[0];
    // dark tear line below the inner corner (cats, dogs), brow ridge in darker fur
    if (s.muzzle === 'katze' || s.muzzle === 'hund' || s.muzzle === 'wolf') { cv.m(x0, b0 - 1, dk); cv.m(x0, b0 - 2, dk); }
    if (s.brows !== 'keine') drawBrows(cv, { ...s, brows: s.brows === 'normal' ? 'dick' : s.brows }, { ey }, box, tier);
  }
  // muzzle front: rows where the scan surface is the muzzle (z clearly in front of the head front)
  const zf = S.zFront;
  let mTop = -1, mBot = -1;
  for (let j = H - 1; j >= 0; j--) {
    const i = cv.cR; const z = S.z[j * S.W + i];
    if (z !== null && z > zf + (lo ? 0 : 1)) { if (mTop < 0) mTop = j; mBot = j; }
  }
  const noseC = s.muzzle === 'katze' || s.muzzle === 'nager' || s.muzzle === 'schwein' ? 0xd07080 : 0x231a18;
  if (mTop >= 0) {
    const nt = mTop;
    switch (s.muzzle) {
      case 'katze': case 'nager':
        if (lo) { cv.m(0, nt, noseC, 1); break; }
        cv.m(0, nt, noseC, 1); cv.m(1, nt, noseC, 1); cv.m(0, nt - 1, noseC, 1);
        cv.m(0, nt + 1, mix(noseC, 0xffffff, 0.3), 1); cv.m(0, nt - 2, dk); cv.m(1, nt - 3, dk); cv.m(2, nt - 3, dk);
        for (const [dx, dy] of [[3, -1], [4, -2]]) cv.m(dx, nt + dy, shade(s.color, 0.6));
        if (s.muzzle === 'nager') { cv.m(0, nt - 4, WHITE, 1); cv.m(0, nt - 5, WHITE, 1); }
        break;
      case 'hund': case 'wolf': case 'baer':
        if (lo) { cv.m(0, nt, noseC, 1); break; }
        { const nw = s.muzzle === 'baer' ? 2 : 1; for (let d = 0; d <= nw; d++) { cv.m(d, nt, noseC, 1); cv.m(d, nt - 1, noseC, 1); } cv.m(0, nt, 0x5a5050, 1); cv.m(0, nt - 2, dk); cv.m(0, nt - 3, dk); cv.m(1, nt - 3, dk); cv.m(2, nt - 4, dk); }
        break;
      case 'pferd':
        if (lo) { cv.m(1, mBot + 1, dk); break; }
        for (const dy of [0, 1]) cv.m(2, mBot + 2 + dy, INK); cv.m(1, mBot + 2, dk);
        for (let d = 0; d <= 3; d++) cv.m(d, mBot, shade(fur, 0.8));
        break;
      case 'reptil':
        cv.m(lo ? 0 : 2, nt, INK); if (!lo) cv.m(2, nt - 1, dk);
        break;
      case 'schwein':
        for (let d = 0; d <= (lo ? 0 : 2); d++) for (let y = mBot; y <= nt; y++) cv.m(d, y, mix(fur, 0xf0a0b0, 0.4));
        if (!lo) { cv.m(1, Math.round((mBot + nt) / 2), shade(0xd07080, 0.45)); }
        break;
      case 'affe':
        if (!lo) { cv.m(1, nt, dk); cv.m(1, nt - 1, dk); for (let d = 0; d <= 3; d++) cv.m(d, mBot + 1, d === 3 ? dk : shade(fur, 0.6)); }
        break;
    }

  }
  // fur markings
  drawMarks(cv, s, { ey, ny: Math.max(0, mTop >= 0 ? mTop : Math.round(H * 0.35)), my: Math.max(0, mBot) }, box.length ? box : [[ey - 1, ey + 1, 2, 4]], tier, false);
}

/* ---------------- face canvas for a scanned front ---------------- */
function paintFront(L, s, ctx) {
  const fr = ctx.head;
  const parts = ctx.jawPart >= 0 ? [ctx.headPart, ctx.jawPart] : [ctx.headPart];
  const animalFront = ctx.kind !== 'humanoid' || s.muzzle !== 'keine';
  // rows: the head box (plus the jaw for animals)
  const vs = L.vs;
  let rows = null;
  if (ctx.jaw) rows = [Math.ceil((ctx.jaw.c[1] - ctx.jaw.h[1]) / vs - 0.5), Math.floor((fr.c[1] + fr.h[1]) / vs - 0.5)];
  // only paint the face itself: voxels of face primitives (head base, muzzle) or skin-coloured ones; helmets,
  // hair, visors and other parts drawn over the face hide it
  const S = scan(L, fr, parts, rows, v => v.f || v.c === s.skin || (s.type === 'tier' || s.type === 'roboter') && v.c === s.color || (ctx.coverColors && ctx.coverColors.includes(v.c)));
  if (S.W < 2 || S.H < 2) return;
  const W = S.W, H = S.H;
  S.zFront = Math.floor((fr.c[2] + fr.h[2]) / vs - 0.5);
  const tier = W >= 14 ? 'hi' : W >= 9 ? 'mid' : 'lo';
  const cv = new Canvas(W, H);
  // rows of the head box inside the canvas (canvas may start lower for the jaw)
  const hy0 = Math.ceil((fr.c[1] - fr.h[1]) / vs - 0.5) - S.Y0, hH = Math.floor((fr.c[1] + fr.h[1]) / vs - 0.5) - Math.ceil((fr.c[1] - fr.h[1]) / vs - 0.5) + 1;
  const R = v => hy0 + rowOf(hH, v);
  if (animalFront && ctx.kind !== 'humanoid') {
    const A = { ey: R(ctx.kind === 'serpent' ? 0.45 : 0.38) };
    drawAnimal(cv, s, A, tier, S);
    apply(L, S, cv);
    return;
  }
  const A = { ey: R(HUMAN_ANCHORS.eye), ny: R(HUMAN_ANCHORS.nose), my: R(HUMAN_ANCHORS.mouth) };
  if (tier === 'lo') { A.ny = A.ey - 1; A.my = Math.max(0, A.ey - 2); }
  if (s.marks.includes('clown')) for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) if (j < R(0.62)) cv.set(i, j, 0xf6f3ee);
  if (s.type === 'roboter') drawRobotPlate(cv, s, A, tier);
  if (s.muzzle !== 'keine') {
    // animal-headed humanoid: animal eyes + muzzle details on a flat head
    drawAnimal(cv, s, { ey: R(0.2) }, tier, S);
    apply(L, S, cv);
    return;
  }
  drawMarks(cv, s, A, [[A.ey - 1, A.ey + 1, 2, 6]], tier, false);
  const eyeBox = drawHumanEyes(cv, s, A, tier);
  drawCheeksAge(cv, s, A, eyeBox, tier);
  drawMarks(cv, s, A, eyeBox, tier, false);
  drawBrows(cv, s, A, eyeBox, tier);
  drawNose(cv, s, A, tier);
  drawBeard(cv, s, A, tier);
  if (s.beard === 'keiner' || !['walross'].includes(s.beard)) drawMouth(cv, s, A, tier);
  if (['schnurrbart', 'zwirbelbart', 'kinnbart', 'spitzbart'].includes(s.beard)) drawBeard(cv, { ...s, beard: s.beard === 'zwirbelbart' ? 'zwirbelbart' : 'schnurrbart' }, A, tier);
  drawMarks(cv, s, A, eyeBox, tier, true);
  drawGlasses(cv, s, eyeBox, tier);
  apply(L, S, cv);
}

/* ---------------- 3D painters (in head-local metres) ---------------- */
// iterate the layer cells of a box given in head units; fn(u, v, w, x, y, z) -> colour | null
function volume(L, fr, lo, hi, fn, part) {
  const vs = L.vs;
  const a0 = [0, 1, 2].map(a => Math.floor((fr.c[a] + lo[a] * fr.h[a]) / vs)), a1 = [0, 1, 2].map(a => Math.floor((fr.c[a] + hi[a] * fr.h[a]) / vs));
  for (let z = a0[2]; z <= a1[2]; z++) for (let y = Math.max(0, a0[1]); y <= a1[1]; y++) for (let x = a0[0]; x <= a1[0]; x++) {
    const u = ((x + 0.5) * vs - fr.c[0]) / fr.h[0], v = ((y + 0.5) * vs - fr.c[1]) / fr.h[1], w = ((z + 0.5) * vs - fr.c[2]) / fr.h[2];
    const r = fn(u, v, w, x, y, z);
    if (r !== null && r !== undefined) { if (r === false) L.del(x, y, z); else L.set(x, y, z, typeof r === 'object' ? r.c : r, part, typeof r === 'object' ? r.e || 0 : 0); }
  }
}
// hanging beards below the chin (in front of the neck)
function beardVolume(L, s, ctx) {
  const fr = ctx.head, st = s.beard, c = s.beardColor, d2 = shade(c, 0.8);
  const vs = L.vs, fz = (fr.c[2] + fr.h[2]) / fr.h[2] - fr.c[2] / fr.h[2]; // = 1
  const len = { kinnbart: 0.22, ziegenbart: 0.3, spitzbart: 0.5, vollbart: 0.3, langer_bart: 1.5, pharao: 0.75 }[st];
  if (!len) return;
  const wid = { kinnbart: 0.2, ziegenbart: 0.16, spitzbart: 0.24, vollbart: 0.85, langer_bart: 0.7, pharao: 0.13 }[st];
  const extra = vs / fr.h[2];
  volume(L, fr, [-1, -1 - len - 0.05, 0.25], [1, -0.98, fz + extra], (u, v, w) => {
    const t = (-1 - v) / len; // 0 at the chin .. 1 at the tip
    if (t < 0 || t > 1) return null;
    const halfW = st === 'pharao' ? wid : st === 'vollbart' ? wid * (1 - t * t * 0.7) : wid * (1 - t * 0.85) + 0.02;
    if (Math.abs(u) > halfW) return null;
    const depth = st === 'pharao' ? 0.28 : 0.5 + (1 - t) * 0.2;
    if (w < fz - depth || w > fz + extra * 0.99) return null;
    if (st === 'pharao') return Math.floor((v * fr.h[1]) / (vs * 2)) % 2 ? s.color : c;
    return h2(Math.floor(u * 9), Math.floor(v * 11)) > 0.75 ? d2 : c;
  }, ctx.headPart);
}
// side paint for full beards / sideburns / stubble on the head sides
function beardSides(L, s, ctx) {
  const st = s.beard; if (!['vollbart', 'langer_bart', 'koteletten', 'stoppeln'].includes(st)) return;
  const fr = ctx.head, vs = L.vs, c = s.beardColor;
  const top = HUMAN_ANCHORS.eye - 0.05;
  const ext = vs / fr.h[0];
  volume(L, fr, [-1 - ext, -1, -0.3], [1 + ext, top, 1], (u, v, w, x, y, z) => {
    if (Math.abs(u) < 1 - ext * 1.2) return null;
    const on = L.get(x, y, z); if (!on || on.p !== ctx.headPart || !(on.f || on.c === s.skin)) return null;
    if (st === 'koteletten' && (w < 0.45 || v < HUMAN_ANCHORS.mouth)) return null;
    if (st === 'stoppeln') return (x + y + z) % 2 && h2(y * 3 + x, z * 7) > 0.5 ? mix(s.skin, c, 0.22) : null;
    if (w < -0.1 + (v + 1) * 0.4 && v > -0.6) return null;
    return h2(y, z) > 0.75 ? shade(c, 0.8) : c;
  }, ctx.headPart);
}

/* ---------------- 3D parts (entries in head units) ---------------- */
const E3 = (shape, at, size, color, o = {}) => ({ on: o.jaw ? 'jaw' : 'head', shape, at, size, color: typeof color === 'number' ? toHex(color) : color, ...(o.mirror ? { mirror: true } : {}), ...(o.rot ? { rot: o.rot } : {}), ...(o.glow ? { glow: true } : {}), ...(o.stripes ? { stripes: o.stripes } : {}), ...(o.axis ? { axis: o.axis } : {}) });
// muzzle shapes: [entries], plus the front z of the upper muzzle (for teeth) and the lip height
function muzzleParts(s, ctx) {
  const m = s.muzzle, fur = s.skin, mc = s.color, out = [];
  const jaw = ctx.jawPart >= 0;
  let lip = { y: -0.55, z: 1.0, x: 0.3 };
  switch (m) {
    case 'katze':
      out.push(E3('ellipsoid', [0, -0.4, 0.92], [0.52, 0.32, 0.34], mc), E3('ellipsoid', [0.2, -0.46, 1.04], [0.24, 0.2, 0.2], mc, { mirror: true }));
      if (jaw) out.push(E3('ellipsoid', [0, -0.8, 0.95], [0.34, 0.16, 0.26], mc, { jaw: true }));
      lip = { y: -0.62, z: 1.18, x: 0.2 }; break;
    case 'hund': case 'wolf': {
      const L2 = m === 'wolf' ? 0.55 : 0.42;
      out.push(E3('box', [0, -0.34, 1.0 + L2 * 0.6], [0.4, 0.3, L2], mc), E3('box', [0, -0.12, 0.95 + L2 * 0.5], [0.3, 0.14, L2 * 0.9], fur));
      if (jaw) out.push(E3('box', [0, -0.72, 0.95 + L2 * 0.6], [0.34, 0.13, L2 * 0.95], mc, { jaw: true }));
      lip = { y: -0.62, z: 1.0 + L2 * 1.6, x: 0.26 }; break;
    }
    case 'baer':
      out.push(E3('ellipsoid', [0, -0.38, 1.05], [0.55, 0.36, 0.42], mc));
      if (jaw) out.push(E3('ellipsoid', [0, -0.74, 1.0], [0.4, 0.16, 0.36], mc, { jaw: true }));
      lip = { y: -0.68, z: 1.4, x: 0.3 }; break;
    case 'pferd':
      out.push(E3('box', [0, -0.3, 1.35], [0.62, 0.6, 0.55], fur), E3('box', [0, -0.48, 1.85], [0.58, 0.42, 0.12], mc));
      if (jaw) out.push(E3('box', [0, -0.82, 1.5], [0.5, 0.18, 0.45], fur, { jaw: true }));
      lip = { y: -0.85, z: 1.9, x: 0.3 }; break;
    case 'reptil':
      out.push(E3('box', [0, -0.42, 1.4], [0.66, 0.26, 0.7], fur), E3('box', [0.42, 0.42, 0.72], [0.28, 0.12, 0.3], shade(fur, 0.8), { mirror: true }));
      if (jaw) out.push(E3('box', [0, -0.78, 1.38], [0.62, 0.14, 0.68], mc, { jaw: true }));
      lip = { y: -0.66, z: 2.05, x: 0.55 }; break;
    case 'elefant': {
      const ring = { color: toHex(shade(fur, 0.85)), axis: 'y', period: 0.12 };
      out.push(E3('cylinder', [0, -0.95, 1.05], [0.3, 0.75, 0.3], fur, { rot: [8, 0, 0], stripes: ring }),
        E3('cylinder', [0, -1.95, 1.22], [0.24, 0.45, 0.24], fur, { rot: [18, 0, 0], stripes: ring }),
        E3('ellipsoid', [0, -2.38, 1.42], [0.24, 0.16, 0.22], shade(fur, 0.8)));
      lip = { y: -0.7, z: 1.0, x: 0.45 }; break;
    }
    case 'schwein':
      out.push(E3('cylinder', [0, -0.32, 1.12], [0.36, 0.28, 0.2], mix(fur, 0xf0a0b0, 0.35), { axis: 'z' }));
      lip = { y: -0.62, z: 1.1, x: 0.3 }; break;
    case 'affe':
      out.push(E3('ellipsoid', [0, -0.45, 0.95], [0.58, 0.4, 0.32], mc));
      if (jaw) out.push(E3('ellipsoid', [0, -0.8, 0.92], [0.4, 0.14, 0.26], mc, { jaw: true }));
      lip = { y: -0.7, z: 1.22, x: 0.3 }; break;
    case 'nager':
      out.push(E3('ellipsoid', [0, -0.35, 1.0], [0.38, 0.3, 0.3], mc));
      lip = { y: -0.6, z: 1.25, x: 0.15 }; break;
    case 'vogel':
      out.push(E3('cone', [0, -0.15, 1.35], [0.32, 0.45, 0.22], s.color === mix(fur, 0xfff4e0, 0.5) ? 0xf2a03a : s.color, { rot: [90, 0, 0] }));
      if (jaw) out.push(E3('cone', [0, -0.42, 1.2], [0.24, 0.3, 0.14], shade(0xf2a03a, 0.85), { rot: [95, 0, 0], jaw: true }));
      lip = { y: -0.3, z: 1.6, x: 0.15 }; break;
  }
  return { out, lip };
}
function earParts(s, ctx) {
  const e = s.ears, sk = s.skin, inner = s.type === 'tier' ? mix(sk, 0xf0a0a8, 0.55) : shade(sk, 0.78), out = [];
  const ev = ctx.kind === 'humanoid' ? -0.05 : 0;
  switch (e) {
    case 'normal': out.push(E3('box', [1.03, ev, 0.05], [0.07, 0.2, 0.15], sk, { mirror: true }), E3('box', [1.08, ev, 0.05], [0.03, 0.11, 0.08], inner, { mirror: true })); break;
    case 'klein': out.push(E3('box', [1.03, ev, 0.05], [0.05, 0.15, 0.11], sk, { mirror: true })); break;
    case 'gross': out.push(E3('ellipsoid', [1.12, ev + 0.02, 0.0], [0.13, 0.32, 0.22], sk, { mirror: true }), E3('ellipsoid', [1.2, ev + 0.02, 0.02], [0.05, 0.2, 0.12], inner, { mirror: true })); break;
    case 'spitz':
      if (ctx.kind === 'humanoid') out.push(E3('box', [1.03, ev, 0.05], [0.07, 0.2, 0.15], sk, { mirror: true }), E3('cone', [1.18, ev + 0.32, -0.08], [0.07, 0.3, 0.12], sk, { mirror: true, rot: [-20, 0, -40] }));
      else out.push(E3('cone', [0.55, 1.3, -0.2], [0.3, 0.62, 0.16], sk, { mirror: true }), E3('cone', [0.55, 1.22, -0.05], [0.17, 0.42, 0.06], inner, { mirror: true }));
      break;
    case 'katze': out.push(E3('cone', [0.56, 1.2, -0.2], [0.42, 0.48, 0.22], sk, { mirror: true }), E3('cone', [0.56, 1.14, 0.0], [0.26, 0.32, 0.06], inner, { mirror: true })); break;
    case 'hund': case 'schlapp': out.push(E3('ellipsoid', [1.04, 0.15, -0.1], [0.14, 0.58, 0.32], shade(sk, 0.82), { mirror: true, rot: [0, 0, 8] })); break;
    case 'rund': out.push(E3('ellipsoid', [0.68, 1.05, -0.2], [0.32, 0.3, 0.15], sk, { mirror: true }), E3('ellipsoid', [0.68, 1.03, -0.07], [0.18, 0.17, 0.06], inner, { mirror: true })); break;
    case 'pferd': out.push(E3('cone', [0.45, 1.25, -0.45], [0.18, 0.5, 0.12], sk, { mirror: true, rot: [0, 0, -12] }), E3('cone', [0.45, 1.2, -0.36], [0.09, 0.32, 0.05], inner, { mirror: true, rot: [0, 0, -12] })); break;
    case 'maus': out.push(E3('ellipsoid', [0.78, 1.05, -0.1], [0.42, 0.42, 0.1], sk, { mirror: true }), E3('ellipsoid', [0.78, 1.05, 0.0], [0.28, 0.28, 0.05], inner, { mirror: true })); break;
    case 'elefant': out.push(E3('ellipsoid', [1.08, -0.05, -0.25], [0.16, 1.0, 0.85], shade(sk, 1.05), { mirror: true }), E3('ellipsoid', [1.16, -0.1, -0.2], [0.08, 0.72, 0.6], mix(sk, 0xd0a0a8, 0.45), { mirror: true })); break;
    case 'hase': out.push(E3('ellipsoid', [0.35, 1.65, -0.2], [0.17, 0.72, 0.1], sk, { mirror: true }), E3('ellipsoid', [0.35, 1.65, -0.1], [0.09, 0.55, 0.05], inner, { mirror: true })); break;
  }
  return out;
}
function hornParts(s) {
  const c = s.hornColor, out = [], tip = shade(c, 0.8);
  switch (s.horns) {
    case 'stier': out.push(E3('cylinder', [1.05, 0.75, 0.15], [0.12, 0.38, 0.12], c, { mirror: true, rot: [0, 0, -70] }), E3('cone', [1.45, 1.05, 0.15], [0.11, 0.35, 0.11], tip, { mirror: true, rot: [0, 0, -15] })); break;
    case 'widder':
      for (let k = 0; k < 7; k++) { const a = k / 6 * Math.PI * 1.6, r = 0.5 - k * 0.045; out.push(E3('ellipsoid', [1.0 + Math.sin(a) * r * 0.3, 0.55 + Math.cos(a) * r, 0.1 - Math.sin(a) * r], [0.2 - k * 0.012, 0.2 - k * 0.012, 0.2 - k * 0.012], k % 2 ? c : shade(c, 0.88), { mirror: true })); }
      break;
    case 'einhorn': out.push(E3('cone', [0, 1.2, 0.7], [0.13, 0.65, 0.13], c === 0xeadfc4 ? 0xf4e6a0 : c, { rot: [35, 0, 0], stripes: { color: toHex(shade(c, 0.8)), axis: 'y', period: 0.04 } })); break;
    case 'geweih':
      out.push(E3('box', [0.55, 1.45, -0.1], [0.06, 0.5, 0.06], c, { mirror: true, rot: [0, 0, -25] }), E3('box', [0.95, 1.95, -0.1], [0.05, 0.35, 0.05], c, { mirror: true, rot: [0, 0, -55] }),
        E3('box', [0.7, 2.05, -0.1], [0.05, 0.3, 0.05], c, { mirror: true, rot: [0, 0, 10] }), E3('box', [0.6, 1.5, 0.15], [0.04, 0.22, 0.04], c, { mirror: true, rot: [35, 0, -10] }));
      break;
    case 'teufel': out.push(E3('cone', [0.48, 1.18, 0.35], [0.13, 0.32, 0.13], c === 0xeadfc4 ? 0xb8202a : c, { mirror: true, rot: [0, 0, -18] })); break;
    case 'nashorn': out.push(E3('cone', [0, -0.05, 1.45], [0.16, 0.5, 0.16], c, { rot: [30, 0, 0] }), E3('cone', [0, 0.3, 1.1], [0.1, 0.25, 0.1], c, { rot: [20, 0, 0] })); break;
    case 'antennen': out.push(E3('box', [0.32, 1.45, 0.1], [0.035, 0.45, 0.035], c, { mirror: true, rot: [0, 0, -14] }), E3('ellipsoid', [0.44, 1.9, 0.1], [0.1, 0.1, 0.1], s.eyeColor, { mirror: true, glow: true })); break;
    case 'klein': out.push(E3('cone', [0.4, 1.1, 0.25], [0.1, 0.2, 0.1], c, { mirror: true, rot: [-10, 0, -10] })); break;
  }
  return out;
}
function teethParts(s, lip, ctx) {
  const T = 0xf4f0e2, out = [], jaw = ctx.jawPart >= 0;
  switch (s.teeth) {
    case 'saebelzaehne':
      out.push(E3('box', [lip.x, lip.y - 0.42, lip.z - 0.12], [0.075, 0.45, 0.075], T, { mirror: true }), E3('cone', [lip.x, lip.y - 0.95, lip.z - 0.12], [0.07, 0.12, 0.07], 0xe0dac8, { mirror: true, rot: [180, 0, 0] }));
      break;
    case 'fangzaehne': out.push(E3('cone', [lip.x, lip.y - 0.12, lip.z - 0.08], [0.06, 0.16, 0.06], T, { mirror: true, rot: [180, 0, 0] })); break;
    case 'giftzaehne': out.push(E3('cone', [lip.x * 0.6, lip.y - 0.12, lip.z - 0.06], [0.04, 0.2, 0.04], T, { mirror: true, rot: [170, 0, 0] })); break;
    case 'hauer': out.push(E3('cone', [lip.x + 0.12, lip.y + 0.1, lip.z - 0.25], [0.07, 0.3, 0.07], T, { mirror: true, rot: [-15, 0, -20], jaw })); break;
    case 'stosszaehne':
      out.push(E3('cylinder', [0.45, -0.95, 1.35], [0.13, 0.62, 0.13], T, { mirror: true, rot: [58, 0, 0] }), E3('cone', [0.45, -0.62, 2.05], [0.11, 0.42, 0.11], T, { mirror: true, rot: [-20, 0, 0] }));
      break;
  }
  if (s.tongue === 'zunge' && jaw) out.push(E3('box', [0.12, lip.y - 0.28, lip.z + 0.02], [0.12, 0.05, 0.16], 0xe0607a, { jaw: true, rot: [-25, 0, 0] }));
  if (s.tongue === 'gespalten') out.push(E3('box', [0, lip.y - 0.18, lip.z + 0.25], [0.035, 0.03, 0.3], 0xe0405a, { jaw }), E3('box', [0.06, lip.y - 0.18, lip.z + 0.58], [0.03, 0.03, 0.08], 0xe0405a, { jaw, mirror: true, rot: [0, 30, 0] }));
  if (s.whiskers === 'schnurrhaare' && s.muzzle !== 'keine') out.push(E3('box', [lip.x + 0.42, lip.y + 0.2, lip.z - 0.16], [0.28, 0.015, 0.015], 0xf4f4f2, { mirror: true, rot: [0, 0, -12] }), E3('box', [lip.x + 0.42, lip.y + 0.08, lip.z - 0.18], [0.28, 0.015, 0.015], 0xe8e4dc, { mirror: true, rot: [0, 0, 8] }));
  return out;
}

/* mouth interior for animals with a jaw: dark roof + teeth on the upper rim, tongue on the jaw (seen when it opens) */
function mouthInterior(L, s, ctx) {
  const fr = ctx.head, vs = L.vs, hp = ctx.headPart, jp = ctx.jawPart;
  const roof = 0x5a1a22, tongue = 0xc04a5a, T = 0xf4f0e2;
  const x0 = Math.floor((fr.c[0] - fr.h[0] * 1.2) / vs), x1 = Math.floor((fr.c[0] + fr.h[0] * 1.2) / vs);
  const y0 = Math.max(0, Math.floor((fr.c[1] - fr.h[1] * 1.6) / vs)), y1 = Math.floor((fr.c[1]) / vs);
  const z0 = Math.floor((fr.c[2] - fr.h[2]) / vs), z1 = Math.floor((fr.c[2] + fr.h[2] * 2.6) / vs);
  const ops = [];
  for (let z = z0; z <= z1; z++) for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
    const v = L.get(x, y, z); if (!v) continue;
    if (v.p === hp) { const b = L.get(x, y - 1, z); if (b && b.p === jp) { const rim = !L.get(x, y - 1, z + 1) || !L.get(x + 1, y - 1, z) || !L.get(x - 1, y - 1, z); ops.push([x, y, z, rim && s.teeth !== 'keine' ? T : roof, hp]); } }
    else if (v.p === jp) { const a = L.get(x, y + 1, z); if (a && a.p === hp) ops.push([x, y, z, tongue, jp]); }
  }
  for (const [x, y, z, c, p] of ops) L.set(x, y, z, c, p);
  // closed-mouth line along the front seam between head and jaw
  for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) {
    for (let z = z1; z >= z0; z--) {
      const v = L.get(x, y, z); if (!v) continue;
      if (v.p === jp) { const a = L.get(x, y + 1, z); if (!a || a.p === hp) { if (!L.get(x, y, z + 1)) L.set(x, y, z, s.teeth === 'zahnreihe' && (x & 1) ? T : shade(s.skin, 0.4), jp); } }
      break;
    }
  }
}

/* ---------------- public: parts for a compiler ---------------- */
/**
 * @param {object} rec recipe
 * @param {{ kind: 'humanoid'|'quadruped'|'serpent', head: {c,h}, jaw?: {c,h}, headPart: number, jawPart: number }} ctx frames in metres
 * @returns {{ pre: object[], paint: (layer) => void, post: object[], posts: ((layer) => void)[], spec: object }}
 *   pre/post: add-style entries (head units, on 'head'|'jaw'); paint: the face painter op; posts: 3D ops after hair
 */
export function faceParts(rec, ctx) {
  const s = resolveFace(rec);
  if (s.type === 'keins') return { pre: [], paint: null, post: [], posts: [], spec: s };
  const pre = [], post = [];
  let lip = { y: -0.55, z: 1.0, x: 0.3 };
  if (s.muzzle !== 'keine') { const mz = muzzleParts(s, ctx); pre.push(...mz.out); lip = mz.lip; }
  pre.push(...earParts(s, ctx));
  post.push(...hornParts(s));
  if (s.muzzle !== 'keine' || s.type === 'tier' || ctx.kind !== 'humanoid') post.push(...teethParts(s, lip, ctx));
  if (s.glasses === 'augenklappe' && ctx.kind === 'humanoid') post.push(E3('box', [0, 0.3, -0.02], [1.035, 0.045, 1.035], 0x141418, { rot: [0, 0, -22] }));
  if ((s.glasses === 'brille' || s.glasses === 'rund' || s.glasses === 'sonnenbrille') && ctx.kind === 'humanoid') post.push(E3('box', [1.02, HUMAN_ANCHORS.eye + 0.1, 0.45], [0.035, 0.035, 0.55], s.glassesColor, { mirror: true }));
  if (s.glasses === 'skibrille' && ctx.kind === 'humanoid') post.push(E3('box', [0, HUMAN_ANCHORS.eye + 0.05, -0.05], [1.04, 0.09, 1.04], s.glassesColor));
  const paint = L => paintFront(L, s, ctx);
  const posts = [];
  if (ctx.kind === 'humanoid' && s.beard !== 'keiner') posts.push(L => { beardSides(L, s, ctx); beardVolume(L, s, ctx); });
  if (ctx.jawPart >= 0) posts.unshift(L => mouthInterior(L, s, ctx));
  // bounds of the 3D ops (head units) for the fine region
  const opBounds = s.beard === 'langer_bart' ? [[-1, -2.6, 0.2], [1, -0.9, 1.1]] : s.beard === 'pharao' ? [[-0.3, -1.85, 0.6], [0.3, -0.9, 1.1]] : s.beard !== 'keiner' ? [[-1, -1.6, 0.2], [1, -0.9, 1.1]] : null;
  return { pre, paint, post, posts, spec: s, opBounds };
}

/* Legacy recipes (no `face`): small boxes on the head front were the old face. They are dropped and replaced by
   the painted face; glowing ones turn the default eyes into glowing eyes of that colour. */
export function legacyFaceFilter(rec) {
  if (!Array.isArray(rec.add)) return { add: [], face: null };
  if (rec.face !== undefined) {
    // with a face spec, small flat boxes on a humanoid's face (what language models still like to add: eyes, mouths,
    // skin patches) would cover the painted face: drop them; deliberate details sit outside this window (at.z > 1.15)
    if ((rec.template || 'humanoid') !== 'humanoid' || (rec.face && rec.face.type === 'keins')) return { add: rec.add, face: null };
    const add = rec.add.filter(a => {
      if (a.on !== 'head' || a.rot || (a.shape && a.shape !== 'box') || a.color === 'team') return true;
      const at = a.at || [0, 0, 0], sz = a.size || [1, 1, 1];
      return !(at[2] >= 0.85 && at[2] <= 1.15 && sz[2] <= 0.2 && sz[0] <= 0.62 && sz[1] <= 0.45 && at[1] >= -0.95 && at[1] <= 0.5 && Math.abs(at[0]) <= 0.8);
    });
    return { add, face: null };
  }
  const tpl = rec.template || 'humanoid', keep = [], dropped = [];
  for (const a of rec.add) {
    const at = a.at || [0, 0, 0], size = a.size || [1, 1, 1];
    const small = Math.max(size[0], size[1]) <= (tpl === 'humanoid' ? 0.34 : 0.3) && size[2] <= 0.2;
    const front = at[2] >= (tpl === 'humanoid' ? 0.9 : 0.85) && at[1] >= -1 && at[1] <= (tpl === 'humanoid' ? 0.5 : 0.7) && Math.abs(at[0]) <= 0.8;
    if (a.on === 'head' && small && front && !a.rot) dropped.push(a); else keep.push(a);
  }
  if (!dropped.length) return { add: rec.add, face: null };
  const glow = dropped.find(a => a.glow);
  const face = {};
  if (glow) { face.eyes = 'gluehend'; face.eyeColor = typeof glow.color === 'string' ? glow.color : glow.color && glow.color.color; }
  return { add: keep, face };
}
